import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface FlashAnnouncement {
  id: string;
  content: string;
  type: string;
  is_active: boolean;
  priority: number;
  duration_minutes: number;
  created_at: string;
  expires_at: string;
  created_by: string | null;
  link_url: string | null;
  link_text: string | null;
  style_preset: string;
  updated_at: string;
}

export const useFlashAnnouncements = () => {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('dismissedFlashAnnouncements');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const { data: announcements = [], refetch } = useQuery({
    queryKey: ['flash-announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flash_announcements')
        .select('*')
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as FlashAnnouncement[];
    },
    staleTime: 30000,
  });

  // Real-time subscription for new announcements
  useEffect(() => {
    const channel = supabase
      .channel('flash_announcements_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'flash_announcements'
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Check for expired announcements every minute
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
      // Clean up old dismissed IDs
      const cleanedIds = Array.from(dismissedIds).filter(id => 
        announcements.some(a => a.id === id)
      );
      if (cleanedIds.length !== dismissedIds.size) {
        const newSet = new Set(cleanedIds);
        setDismissedIds(newSet);
        localStorage.setItem('dismissedFlashAnnouncements', JSON.stringify(cleanedIds));
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [refetch, announcements, dismissedIds]);

  const dismissAnnouncement = (id: string) => {
    const newDismissed = new Set(dismissedIds);
    newDismissed.add(id);
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedFlashAnnouncements', JSON.stringify(Array.from(newDismissed)));
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedIds.has(a.id));

  return {
    announcements: visibleAnnouncements,
    dismissAnnouncement,
    refetch
  };
};
